import { ReviewItem } from '@/types/api';
import { Button } from '@/components/atoms/NewPcButton';
import { useMembershipPreviewMode } from '@/components/membership/MembershipContentLock';

import useCopy from '@/hooks/useCopy';
import { useToast } from '@/hooks/V3/useToast';

import CopyIcon from '@/assets/icons/OrderDetails/eva_copy-outline.svg?react';
import MARKET_GUIDE_DATA, { type GuideLink } from '@/contents/marketGuide';

import ExternalIcon from '@/assets/icons/OrderDetails/external-link-fill.svg?react';

import GenerateAnswer from './GenerateAnswer';
import SubmitButton from './SubmitButton';
import SelectTemplate from './SelectTemplate';

export interface AnswerControllerProps {
  comment: string;
  setComment?: (comment: string) => void; // Optional for AutoResizeTextarea
  review: ReviewItem;
  handleRegister: () => void; // Optional for SubmitButton
  isLoading?: boolean; // Optional for SubmitButton
}

const AnswerController = ({
  comment,
  setComment,
  review,
  handleRegister,
  isLoading,
}: AnswerControllerProps) => {
  const isUnanswerable = !review.IsMarketCommentEnable;
  const isPreviewMode = useMembershipPreviewMode();
  const { copyToClipboard } = useCopy({
    toastMessage: '복사가 완료되었습니다',
  });
  const { showToast } = useToast();

  const getSellerPageUrl = (marketNameEnglish: string) => {
    const marketData = MARKET_GUIDE_DATA[marketNameEnglish];
    if (!marketData) return null;
    // linkGuide 타입을 찾아서 linkUrl 반환
    const linkGuide = marketData.find(
      (entry): entry is GuideLink => entry.type === 'linkGuide' && Boolean(entry.contents.linkUrl),
    );
    return linkGuide?.contents.linkUrl ?? null;
  };

  // 컴포넌트 내에서
  const handleNavigateToSeller = () => {
    if (isPreviewMode) {
      showToast('warning', '체험 중에는 판매자 페이지로 이동할 수 없어요.');
      return;
    }

    const url = getSellerPageUrl(review.MarketNameEnglish);
    if (url) {
      window.open(url, '_blank');
    } else {
      // fallback: smartstore 등 직접 패턴 생성
      if (review.MarketNameEnglish === 'smartstore') {
        window.open(`https://smartstore.naver.com/`, '_blank');
      } else if (review.MarketNameEnglish === 'cafe24') {
        window.open(`https://eclogin.cafe24.com/Shop/`, '_blank');
      } else {
        // 없는 경우 안내
        alert('판매자 페이지 링크를 찾을 수 없습니다.');
      }
    }
  };

  return (
    <div className="flex min-w-0 items-start justify-between gap-3">
      <div className="scrollbar flex min-w-0 flex-1 items-start gap-[8px] overflow-x-auto pb-1">
        <div className="shrink-0">
          <GenerateAnswer review={review} setComment={setComment} />
        </div>
        <div className="shrink-0">
          <SelectTemplate setComment={setComment} />
        </div>
        <Button
          onClick={() => copyToClipboard(comment)}
          variant="secondary"
          size="xsmall"
          className="h-10 w-10 shrink-0 p-0 disabled:text-gr-400"
          disabled={!comment}
        >
          <CopyIcon />
        </Button>
      </div>

      {isUnanswerable ? (
        <Button
          onClick={handleNavigateToSeller}
          variant="secondary"
          size="xsmall"
          className="shrink-0 gap-[6px]"
        >
          판매자 페이지
          <ExternalIcon />
        </Button>
      ) : (
        <div className="shrink-0">
          <SubmitButton handleRegister={handleRegister} isLoading={isLoading} comment={comment} />
        </div>
      )}
    </div>
  );
};

export default AnswerController;
